import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useBuildVariantProps from '../../../modules/theme/useBuildVariantProps';
import { useControlledValue } from '../FormField/useControlledValue';
import styles from './ChipInput.module.scss';
import { ChipInputProps } from './ChipInput.types';
import { ChipProps } from '../../atoms/Chip/Chip.types';
import { createChipProps, createCheckboxItemProps, toggleOptionInArray, addUniqueOption, handleEnterKeyForChip, normalizeToOptionLike, findOptionByKey, normalizeToOptionLikeKey, convertKeysToOriginalFormat, filterOptionsByInput, createOptimisticItem } from './ChipInput.utils';
import { OptionLike, OptionLikeKey } from './ChipInput.types';

const usePresenter = <T extends string[] | OptionLike[]>(props: ChipInputProps<T>) => {
  const {
    inputValue: controlledInputValue,
    onInputChange,
    options,
    onValueChange,
    value: controlledValues,
    disabled = false,
    invalid = false,
    onAddItem,
    onDeleteItem,
  } = props;
  // Normalizes options by converting strings to OptionLike objects
  const normalizedOptions = useMemo(() => normalizeToOptionLike(options || []), [options]);
  // Normalizes values by converting keys to OptionLike objects
  const normalizedValues = useMemo(() => normalizeToOptionLikeKey(controlledValues ?? []), [controlledValues]);
  
  // Internal state for options
  const [internalOptions, setInternalOptions] = useState<OptionLike[]>(normalizedOptions);
  // Internal state for input value
  const [internalInputValue, setInternalInputValue] = useState<string>('');

  // Internal state for selected keys
  const { value: internalSelectedKeys, onValueChange: setInternalSelectedKeys } = useControlledValue<OptionLikeKey[]>({ 
    value: normalizedValues, 
    onValueChange: (keys) => {
      // Converts keys to original format and calls onValueChange
      if (onValueChange) {
        const originalFormat = convertKeysToOriginalFormat(keys, controlledValues, internalOptions);
        onValueChange(originalFormat);
      }
    }, 
    defaultValue: [],
  });

  // Update internal options when props.options change
  useEffect(() => {
    setInternalOptions(normalizedOptions);
  }, [normalizedOptions]);

  const inputRef = useRef<HTMLInputElement>(null);
  const inputValue = controlledInputValue ?? internalInputValue;
  
  const handleInputValueChange = useCallback((value: string) => {
    if (onInputChange) {
      onInputChange(value);
    } else {
      setInternalInputValue(value);
    }
  }, [onInputChange]);

  // Always filter options based on input value
  const filteredOptions = useMemo(() => {
    return filterOptionsByInput(internalOptions, inputValue);
  }, [internalOptions, inputValue]);

  /**
   * Handle option click
   */
  const handleSelect = useCallback((selectedOptionKey: OptionLikeKey) => {
    setInternalSelectedKeys(prev => {
      return toggleOptionInArray(prev || [], selectedOptionKey);
    });
  }, [setInternalSelectedKeys]);

  /**
   * Handle add new option with async support
   */
  const handleAddNewOption = useCallback(async (inputValue: string) => {
    // Use onAddItem (async) if available
    if (onAddItem) {
      // Async operation with optimistic update
      const tempOption = createOptimisticItem(inputValue);
      
      // Add temp option to internal options
      setInternalOptions(prev => {
        const updated = addUniqueOption(prev, tempOption);
        return updated;
      });
      
      // Add temp option to internal selected keys
      setInternalSelectedKeys(prev => {
        const updated = prev ? [...prev, tempOption.key] : [tempOption.key];
        return updated;
      });
      
      try {
        const realOption = await onAddItem(inputValue);
        
        // Update internal options with real option
        setInternalOptions(prev => {
          const updated = prev.map(opt => opt.key === tempOption.key ? realOption : opt);
          return updated;
        });
        
        // Update internal selected keys with real option key
        setInternalSelectedKeys(prev => {
          const updated = (prev || []).map(key => key === tempOption.key ? realOption.key : key);
          return updated;
        });
      } catch (error) {
        // Delete temp option if error occurs
        if (onDeleteItem) {
          await onDeleteItem(tempOption.key);
        }
        
        // Remove temp option on error
        setInternalOptions(prev => {
          const updated = prev.filter(opt => opt.key !== tempOption.key);
          return updated;
        });
        
        // Remove temp option from internal selected keys
        setInternalSelectedKeys(prev => {
          const updated = (prev || []).filter(key => key !== tempOption.key);
          return updated;
        });
      }
    } else {
      // Create default OptionLike from input value
      const newOption: OptionLike = { key: inputValue, label: inputValue };
      
      // Add new option to internal options
      setInternalOptions((prev) => {
        const updated = addUniqueOption(prev, newOption);
        return updated;
      });
      
      // Add new option to internal selected keys
      setInternalSelectedKeys(prev => {
        const updated = [...(prev || []), newOption.key];
        return updated;
      });
    }
  }, [onAddItem, onDeleteItem, setInternalOptions, setInternalSelectedKeys]);

  /**
   * Enhanced key handler with backspace deletion and enter for adding
   */
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace deletion: remove last chip when input is empty
    if (event.key === 'Backspace' && !inputValue.trim() && internalSelectedKeys?.length) {
      const lastKey = internalSelectedKeys[internalSelectedKeys.length - 1];
      removeChip(lastKey);
      return;
    }
    
    // Enter key: add new item
    handleEnterKeyForChip(
      event,
      inputValue,
      (trimmedValue) => {
        // Check if option already exists
        const existingOption = internalOptions.find(opt => opt.key === trimmedValue || opt.label === trimmedValue);
        let optionKey: OptionLikeKey;
        
        if (existingOption) {
          optionKey = existingOption.key;
          // Add to selected values if not already selected
          setInternalSelectedKeys((prev) => {
            if (prev?.includes(optionKey)) {
              return prev;
            }
            return [...(prev || []), optionKey];
          });
        } else {
          // Add new option. it will be added to internal options and internal selected keys
          handleAddNewOption(trimmedValue);
        }
      },
      () => setInternalInputValue('')
    );
  }, [inputValue, internalOptions, handleAddNewOption, setInternalSelectedKeys, internalSelectedKeys]);

  /**
   * Handle removing a selected option
   */
  const removeChip = useCallback((keyToRemove: OptionLikeKey) => {
    setInternalSelectedKeys((prev) => {
      const newKeys = (prev || []).filter(key => key !== keyToRemove);
      return newKeys;
    });
  }, [setInternalSelectedKeys]);

  /**
   * Prepare chip data only
   */
  const chips = useMemo(() => {
    if (internalSelectedKeys) {
      return internalSelectedKeys.map((key): ChipProps => {
        const option = findOptionByKey(internalOptions, key);
        if (option) {
          return createChipProps(option, removeChip, styles.chip);
        }
        // Fallback if option not found (shouldn't happen but for safety)
        return createChipProps({ key, label: String(key) }, removeChip, styles.chip);
      });
    }
    return [];
  }, [internalSelectedKeys, internalOptions, removeChip]);
  
  /**
   * This is to focus in the input when chip input is clicked
   */
  const handleChipInputAreaClick = useCallback(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  /**
   * Prepare option list for Chip Input Context Menu using filtered options
   */
  const optionList = useMemo(() => {
    if (filteredOptions) {
      return filteredOptions.map((option) => {
        const isChecked = internalSelectedKeys?.includes(option.key) ?? false;
        return createCheckboxItemProps(option, isChecked, handleSelect, styles.checkboxItem);
      });
    }
    return [];
  }, [filteredOptions, internalSelectedKeys, handleSelect]);

  /**
   * Prepare variant props for Chip Input
   */
  const variantProps = useBuildVariantProps(
    'chip-input',
    {},
    {
      disabled,
      invalid,
    },
  );

  return {
    handleChipInputAreaClick,
    handleKeyDown,
    inputValue,
    onValueChange: handleInputValueChange,
    chips,
    inputRef,
    optionList,
    variantProps,
    disabled
  };
};

export default usePresenter;